import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";

import { FullScreenLayer, Icons, LoadingState, LockBadge, MessageState, useFeedback } from "../../../../../ds";
import { PageHistoryResponseDTO } from "../../../models/services/PageHistoryResponseDTO";
import { PageService } from "../../../services/PageService";
import { LockMark } from "../../../../locks/models/LockDTO";
import { historyLockLabel } from "../../../../locks/utils/historyLines";
import { renderPage, type PageFormat } from "../../../../../astromark";
import { RenderedHtml } from "../reader/RenderedHtml";

export interface IPageHistoryProps {
    pageId: string;
    spaceId: string;
    /** The page's title (the layer's heading). */
    pageTitle?: string;
    /** The page's lock badge, when it is locked. */
    lockMark?: LockMark;
    format?: PageFormat;
    onClose: () => void;
}

const pagesSvc = new PageService();

export interface IHistoryContent {
    key: number;
    createdAt: string;
    oldTitle: string;
    oldContent: string;
    newTitle: string;
    newContent: string;
    authorName: string;
    /** Set for lock lines (lock / unlock / ...), which carry no content. */
    lockLabel?: string;
}

/** History lines, newest first, with their lock label. */
export function mapToHistoryContent(values: PageHistoryResponseDTO[]): IHistoryContent[] {
    return values
        .map((r, idx) => ({
            key: idx,
            newContent: r.newContent,
            newTitle: r.newTitle,
            oldContent: r.oldContent,
            oldTitle: r.oldTitle,
            createdAt: r.createdAt,
            authorName: r.authorName,
            lockLabel: historyLockLabel(r),
        }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function when(iso: string, format = "d LLL yyyy HH:mm"): string {
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toFormat(format) : iso;
}

function Column(props: { label: string; content: string; format?: PageFormat; empty: string }) {
    const html = useMemo(() => (props.content ? renderPage({ body: props.content, format: props.format }).html : ""), [props.content, props.format]);
    return (
        <section className="ark-history-column" aria-label={props.label}>
            <span className="ow-eyebrow">{props.label}</span>
            {html ? <RenderedHtml html={html} className="ark-prose" /> : <p className="ark-muted ark-history-blank">{props.empty}</p>}
        </section>
    );
}

/**
 * Page history (board "Ark-History"): a full-screen layer with the versions on the left (newest first, lock lines marked)
 * and the chosen version beside the one before it, in two labelled columns: "This version" and "Previous version".
 */
export function PageHistoryPopup(props: IPageHistoryProps) {
    const feedback = useFeedback();
    const [historyContents, setHistoryContents] = useState<IHistoryContent[]>();
    const [selectedVersion, setSelectedVersion] = useState<number>(0);

    useEffect(() => {
        if (!props.spaceId || !props.pageId) return;
        pagesSvc
            .getPageHistory(props.spaceId, props.pageId)
            .then((result) => {
                const mapped = mapToHistoryContent(result);
                setHistoryContents(mapped);
                if (mapped.length > 0) setSelectedVersion(mapped[0].key);
            })
            .catch((error) => {
                setHistoryContents([]);
                feedback.error(error?.code ?? error, "Error!");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.spaceId, props.pageId]);

    const list = historyContents ?? [];
    const index = list.findIndex((m) => m.key === selectedVersion);
    const selected = index >= 0 ? list[index] : undefined;
    // the version this one replaced: the next older content line
    const previous = index >= 0 ? list.slice(index + 1).find((m) => !m.lockLabel) : undefined;

    const title = (
        <span className="ark-history-title">
            {props.pageTitle ? `Page history — ${props.pageTitle}` : "Page history"}
            {props.lockMark ? <LockBadge state={props.lockMark.own ? "own" : "inherited"} by={props.lockMark.lockTitle} size="md" /> : null}
        </span>
    );

    return (
        <FullScreenLayer
            className="ark-history"
            eyebrow={historyContents ? `${list.length} ${list.length === 1 ? "entry" : "entries"} · newest first` : "Page history"}
            title={title}
            onClose={props.onClose}
        >
            {!historyContents ? <LoadingState text="Loading the history…" /> : null}
            {historyContents && list.length === 0 ? (
                <MessageState title="No history yet" text="Edits to this page will be listed here." />
            ) : null}
            {selected ? (
                <div className="ark-history-grid">
                    <nav className="ark-history-versions" aria-label="Versions">
                        <span className="ow-eyebrow">Versions <span className="ark-history-count">{list.length}</span></span>
                        <ol>
                            {list.map((m, i) => (
                                <li key={m.key}>
                                    <button
                                        type="button"
                                        className={m.key === selectedVersion ? "ark-history-version is-selected" : "ark-history-version"}
                                        aria-current={m.key === selectedVersion ? "true" : undefined}
                                        onClick={() => setSelectedVersion(m.key)}
                                    >
                                        <span className="ark-history-when">
                                            <strong>{when(m.createdAt)}</strong>
                                            {m.authorName ? <span> · {m.authorName}</span> : null}
                                        </span>
                                        {i === 0 && !m.lockLabel ? <span className="ark-history-tag is-current">Current</span> : null}
                                        {m.lockLabel ? (
                                            <span className="ark-history-tag">
                                                <Icons.lock size={12} />
                                                {m.lockLabel}
                                            </span>
                                        ) : null}
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </nav>

                    <div className="ark-history-main">
                        <div className="ark-history-main-head">
                            <h3>
                                {when(selected.createdAt)}
                                {selected.authorName ? ` · ${selected.authorName}` : ""}
                            </h3>
                            {selected.lockLabel ? null : selected.oldTitle && selected.newTitle && selected.oldTitle !== selected.newTitle ? (
                                <p className="ark-muted">Renamed from “{selected.oldTitle}” to “{selected.newTitle}”.</p>
                            ) : (
                                <p className="ark-muted">Compared with the version before it.</p>
                            )}
                        </div>
                        {selected.lockLabel ? (
                            <p className="ark-history-lock-line">
                                <Icons.lock size={14} />
                                {selected.lockLabel}
                                {selected.authorName ? ` by ${selected.authorName}` : ""}: a lock change, with no content.
                            </p>
                        ) : (
                            <div className="ark-history-columns">
                                <Column
                                    label={`This version (${when(selected.createdAt, "d LLL")})`}
                                    content={selected.newContent}
                                    format={props.format}
                                    empty="This version is empty."
                                />
                                <Column
                                    label={previous ? `Previous version (${when(previous.createdAt, "d LLL")})` : "Previous version"}
                                    content={selected.oldContent}
                                    format={props.format}
                                    empty="There is no previous version: the page was created here."
                                />
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </FullScreenLayer>
    );
}
